import { useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ProtectedRoute } from './ProtectedRoute'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { ROUTES } from '@/routes/routes'
import api from '@/api/client'
import { currentHousehold } from '@/lib/currentHousehold'

interface Props {
  children: React.ReactNode
}

export function RequireHousehold({ children }: Props) {
  const { data: user, isLoading } = useCurrentUser()
  const navigate = useNavigate()

  useEffect(() => {
    const id = api.interceptors.response.use(
      (res) => res,
      (err) => {
        const code = err?.response?.data?.code
        if (
          err?.response?.status === 403 &&
          (code === 'NO_HOUSEHOLD' || code === 'NO_HOUSEHOLD_HEADER')
        ) {
          navigate(ROUTES.APP.ONBOARDING, { replace: true })
        }
        return Promise.reject(err)
      }
    )
    return () => api.interceptors.response.eject(id)
  }, [navigate])

  // A household already on this device is enough for every request to carry its header, so the
  // page's own queries start alongside /users/me instead of after it — one round trip less on
  // every app open. The household-null check below still redirects once /users/me arrives.
  if (isLoading && !currentHousehold.get()) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading your account…</p>
      </div>
    )
  }

  if (user && user.household === null) {
    return <Navigate to={ROUTES.APP.ONBOARDING} replace />
  }

  return <>{children}</>
}

export function RequireHouseholdRoute({ children }: Props) {
  return (
    <ProtectedRoute>
      <RequireHousehold>{children}</RequireHousehold>
    </ProtectedRoute>
  )
}
